import { action, makeObservable, observable } from 'mobx';
import { _noop } from 'utils/function.utils';

export enum DefaultPollingStatus {
  Stopped = 'Stopped',
  Running = 'Running',
  Success = 'Success',
  Failure = 'Failure',
}

export interface ProcessingLimits {
  MaxTime: number,
  MaxErrors: number,
  PollTimeStart: number
}

const ProcessingLimitsDefaults: ProcessingLimits = {
  MaxTime: 3600000,
  MaxErrors: 10,
  PollTimeStart: 10000
}

export interface PollingStats {
  pollCount: number,
  duration: number,
  errorCount: number
}

const PollingDefaults: PollingStats = {
  pollCount: 0,
  duration: 10000,
  errorCount: 0
}

export interface PollingStatusCheck {
  onPending: (res: any) => boolean;
  onSuccess: (res: any) => boolean;
  onFailed: (res: any) => boolean;
}

export interface PollingStatusAction {
  onPending: (res?: any) => void;
  onSuccess: (res?: any) => void;
  onFailed: (error?: string) => void;
}
const PollingStatusActionDefault = {
  onPending: _noop,
  onSuccess: _noop,
  onFailed: _noop,
}

export class PollingStore {
  pollingStats = PollingDefaults;
  pollingLimits = ProcessingLimitsDefaults;
  pendingPoll!: NodeJS.Timer;
  message!: string | null;
  status = DefaultPollingStatus.Stopped;
  pollRequest!: () => Promise<any>;

  // NOTE: Actions & Checks should be populated by the calling service
  actions: PollingStatusAction;
  checks: PollingStatusCheck;

  constructor(pollRequest: () => Promise<any>, checks: PollingStatusCheck, actions: PollingStatusAction = PollingStatusActionDefault) {
    makeObservable(this, {
      pollingStats: observable,
      pendingPoll: observable,
      message: observable,
      actions: observable,
      startPoll: action,
    });

    this.pollRequest = pollRequest;
    this.checks = checks;
    this.actions = actions;
  }

  startPoll() {
    if (this.pendingPoll) {
      this.clearPoll();
    }

    this.status = DefaultPollingStatus.Running;
    this.message = null;

    this.pendingPoll = setInterval(() => {
      if ((this.pollingStats.pollCount * this.pollingLimits.PollTimeStart) >= this.pollingLimits.MaxTime) {
        this.onFailed('Maximum time of poll reached.');
        return;
      }

      this.pollingStats.pollCount++;

      this.checkStatus();

    }, this.pollingLimits.PollTimeStart);
  }

  clearPoll() {
    this.pollingStats = PollingDefaults;

    if (this.pendingPoll) {
      clearInterval(this.pendingPoll);
    }
  }

  clearState() {
    this.clearPoll();
    this.status = DefaultPollingStatus.Stopped;
    this.message = null;
  }

  changeLimits(limits: ProcessingLimits) {
    this.pollingLimits = limits
  }

  private onPending(statusResponse?: any) {
    this.status = DefaultPollingStatus.Running;
    this.actions.onPending(statusResponse);
  }

  private onSuccess(statusResponse?: any) {
    this.status = DefaultPollingStatus.Success;
    this.actions.onSuccess(statusResponse);
    this.clearPoll();
  }

  private onFailed(message: string = 'Unknown error') {
    this.message = message;
    this.status = DefaultPollingStatus.Failure;
    this.actions.onFailed(message);
    this.clearPoll();
  }

  private checkStatus() {
    this.pollRequest()
      .then(
        (statusResponse: any) => {
          if (this.checks.onPending(statusResponse)) {
            this.pollingStats.errorCount = 0;
            this.onPending(statusResponse);
            return;
          }

          if (this.checks.onSuccess(statusResponse)) {
            this.pollingStats.errorCount = 0;
            this.onSuccess(statusResponse);
            return;
          }

          if (this.checks.onFailed(statusResponse)) {
            this.pollingStats.errorCount = 0;
            this.onFailed('Status Returned Failed');
            return;
          }

          this.handlePollingError();
        }
      )
      .catch(error => {
        this.handlePollingError();
      });
  }

  private handlePollingError() {
    this.pollingStats.errorCount += 1;
    if (this.pollingStats.errorCount >= this.pollingLimits.MaxErrors) {
      this.onFailed('Max error count reached on poll');
    }
  }
}